import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Flight,
  Hotel,
  LocationOn,
  CalendarMonth,
  InsertDriveFile,
  CheckCircleOutline,
  AccessTime,
  CancelOutlined,
  Search,
  Add,
  FileDownload,
  Visibility,
  Edit,
  FilterAlt, // <-- Add this line
  Refresh,
} from "@mui/icons-material";
import FlightHotelIcon from './FlightHotelIcon';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TablePagination from '@mui/material/TablePagination';
import {
  getTransactionsAPI,
  getTransactionDetailsAPI,
} from '../operations/transactionService';
import ModifyBookingWorkflow from './ModifyBookingWorkflow';
import CancelBookingWorkflow from './CancelBookingWorkflow';

/* ================= TYPES ================= */
// SMB-XXXX enrichment: seven new fields surfaced by the transactions endpoint
// sourced from the latest modify_transactions row per booking. See
// docs/api-gateway/modify-booking-endpoints.md for the source spec.
type EnrichedReqType = "new" | "modify" | "refund" | "cancel";
type EnrichedModState =
  | "new_request"
  | "checking"
  | "quote_ready"
  | "sent_to_customer"
  | "customer_approved"
  | "customer_rejected"
  | "refund_pending"
  | "refund_complete";

// Cancellation status surfaced on the transactions row (latest cancel row per
// booking). Mirrors the backend cancel state machine. See
// docs/CANCEL_BOOKING_GATEWAY_SPEC.md.
type EnrichedCancelState =
  | "CANCEL_REQUESTED"
  | "QUOTE_PENDING"
  | "QUOTE_READY"
  | "SENT_TO_CUSTOMER"
  | "CUSTOMER_APPROVED"
  | "CANCELLING"
  | "SUPPLIER_CONFIRMED"
  | "REFUND_PENDING"
  | "CANCELLED"
  | "CANCEL_FAILED"
  | "QUOTE_EXPIRED"
  | "AUTO_CANCELLED"
  | "FAILED";

interface EnrichedQuote {
  fareDiff: number;
  changeFee: number;
  markup: number;
  customerTotal: number;
}

interface EnrichedReqDates {
  outbound: string | null;
  inbound: string | null;
}

interface ApiTransaction {
  productType: "flight" | "hotel";
  itineraryId: string;
  name: string;
  bkdate: string;
  paymentStatus: string;
  price: string;
  startDate: string;
  endDate: string;
  supplierName: string;
  supplierSource: string;
  tripType?: string;
  sup_conf_Id?: string;
  bkStatus?: string;
  email?: string;
  userId?: string | null; // customer id, required by /request (if backend sends it on the row)
  // SMB-XXXX enrichment fields
  reqType?: EnrichedReqType;
  modState?: EnrichedModState | null;
  bookStatus?: "inprogress" | "confirmed";
  payStatus?: "pending" | "succeeded";
  reqDates?: EnrichedReqDates | null;
  quote?: EnrichedQuote | null;
  modifyTransactionId?: string | null;
  // Cancellation enrichment (latest cancel row per booking)
  cancelState?: EnrichedCancelState | null;
  cancelTransactionId?: string | null;
}

const MOD_STATE_CHIP: Record<EnrichedModState, { label: string; bg: string; text: string }> = {
  new_request: { label: "Awaiting action", bg: "#EFF6FF", text: "#2563EB" },
  checking: { label: "Checking supplier", bg: "#FFFBEB", text: "#D97706" },
  quote_ready: { label: "Quote ready", bg: "#F5F3FF", text: "#7C3AED" },
  sent_to_customer: { label: "Sent to customer", bg: "#ECFDF5", text: "#059669" },
  customer_approved: { label: "Approved", bg: "#ECFDF5", text: "#059669" },
  customer_rejected: { label: "Declined", bg: "#FEF2F2", text: "#DC2626" },
  refund_pending: { label: "Refund pending", bg: "#FFFBEB", text: "#D97706" },
  refund_complete: { label: "Refund complete", bg: "#ECFDF5", text: "#059669" },
};

const REQ_TYPE_CHIP: Record<EnrichedReqType, { label: string; bg: string; text: string }> = {
  new: { label: "New", bg: "#EFF6FF", text: "#2563EB" },
  modify: { label: "Modify", bg: "#FFFBEB", text: "#C2410C" },
  refund: { label: "Refund", bg: "#FEF2F2", text: "#B91C1C" },
  cancel: { label: "Cancel", bg: "#FEF2F2", text: "#991B1B" },
};

const CANCEL_STATE_CHIP: Record<EnrichedCancelState, { label: string; bg: string; text: string }> = {
  CANCEL_REQUESTED: { label: "Cancel requested", bg: "#FEF2F2", text: "#991B1B" },
  QUOTE_PENDING: { label: "Quote pending", bg: "#FFFBEB", text: "#92400E" },
  QUOTE_READY: { label: "Quote ready", bg: "#F5F3FF", text: "#7C3AED" },
  SENT_TO_CUSTOMER: { label: "Sent to customer", bg: "#ECFDF5", text: "#065F46" },
  CUSTOMER_APPROVED: { label: "Customer approved", bg: "#ECFDF5", text: "#065F46" },
  CANCELLING: { label: "Cancelling", bg: "#FFFBEB", text: "#92400E" },
  SUPPLIER_CONFIRMED: { label: "Supplier confirmed", bg: "#EFF6FF", text: "#1E40AF" },
  REFUND_PENDING: { label: "Refund pending", bg: "#FFFBEB", text: "#92400E" },
  CANCELLED: { label: "Cancelled", bg: "#ECFDF5", text: "#065F46" },
  CANCEL_FAILED: { label: "Cancel failed", bg: "#FEF2F2", text: "#991B1B" },
  QUOTE_EXPIRED: { label: "Quote expired", bg: "#F4F5F9", text: "#6B7280" },
  AUTO_CANCELLED: { label: "Auto-cancelled", bg: "#F4F5F9", text: "#6B7280" },
  FAILED: { label: "Failed", bg: "#FEF2F2", text: "#991B1B" },
};

// The transactions API uses its own key names for the post-booking enrichment:
// `requestType` (modify | cancel | refund), the STATE under `postStatus` (NOT
// `modState`), and a SINGLE post-booking transaction id under `modifyTxnId`
// regardless of flow. Normalize all of these at the fetch boundary and route by
// requestType, so the Process buttons / state chips / tab filters work. Falls
// back to our own names if the backend ever aligns them.
function normalizeEnrichment(row: ApiTransaction): ApiTransaction {
  const r = row as ApiTransaction & {
    requestType?: EnrichedReqType;
    postStatus?: string | null;
    modifyTxnId?: string | null;
    cancelTxnId?: string | null;
  };
  const reqType = row.reqType ?? r.requestType;
  // One id field carries both flows; requestType decides where it belongs.
  const txnId =
    row.modifyTransactionId ?? r.modifyTxnId ?? row.cancelTransactionId ?? r.cancelTxnId ?? null;
  const isCancel = reqType === "cancel";
  // Only modify/refund rows own a modify txn id; cancel rows a cancel txn id.
  // A "new" booking can carry a stray modifyTxnId in the payload -- gating on
  // reqType prevents it from wrongly showing "Process modify".
  const isModify = reqType === "modify" || reqType === "refund";
  // State lives in `postStatus` (e.g. "quote_ready"); modify keeps it as-is,
  // cancel uppercases to match the CANCEL_STATE_CHIP keys (e.g. QUOTE_READY).
  const stateRaw = (r.postStatus ?? row.modState ?? undefined) || undefined;
  return {
    ...row,
    reqType,
    modifyTransactionId: isModify ? txnId : null,
    cancelTransactionId: isCancel ? (r.cancelTxnId ?? txnId) : null,
    modState: isCancel ? null : ((stateRaw as EnrichedModState | undefined) ?? null),
    cancelState: isCancel && stateRaw ? (stateRaw.toUpperCase() as EnrichedCancelState) : null,
  };
}

// Crash-safe enrichment chips. The backend reuses a single `modState` field for
// both flows, so a cancel row may carry a value that is not in MOD_STATE_CHIP;
// look up defensively and render nothing for unknown values rather than throwing.
const EnrichmentBadges: React.FC<{ row: ApiTransaction }> = ({ row }) => {
  const reqChip = row.reqType && row.reqType !== "new" ? REQ_TYPE_CHIP[row.reqType] : undefined;
  const cancelKey: EnrichedCancelState | undefined =
    row.reqType === "cancel"
      ? row.cancelState ?? (row.modState?.toUpperCase() as EnrichedCancelState | undefined)
      : undefined;
  const stateChip = cancelKey
    ? CANCEL_STATE_CHIP[cancelKey]
    : row.modState
      ? MOD_STATE_CHIP[row.modState]
      : undefined;
  if (!reqChip && !stateChip) return null;
  const chipSx = (c: { bg: string; text: string }) => ({
    height: 18,
    fontSize: 9,
    fontWeight: 600,
    background: c.bg,
    color: c.text,
  });
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
      {reqChip && <Chip size="small" label={reqChip.label} sx={chipSx(reqChip)} />}
      {stateChip && <Chip size="small" label={stateChip.label} sx={chipSx(stateChip)} />}
    </Box>
  );
};

const getPriceColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return { textColor: "#059669", bgColor: "#d1fae5", borderColor: "#a7f3d0" };
      case "on hold":
        return { textColor: "#c24153", bgColor: "#ffedd5", borderColor: "#fdba74" };
      case "pending":
        return { textColor: "#d97706", bgColor: "#fef3c7", borderColor: "#fde68a" };
      case "cancelled":
        return { textColor: "#dc2626", bgColor: "#fee2e2", borderColor: "#fecaca" };
      default:
        return { textColor: "inherit", bgColor: "inherit", borderColor: "inherit" };
    }
  };

  const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case "succeeded":
      return <CheckCircleOutline fontSize="small" />;
    case "pending":
      return <AccessTime fontSize="small" />;
    case "on hold":
      return <AccessTime fontSize="small" />;
    case "cancelled":
      return <CancelOutlined fontSize="small" />;
    default:
      return null;
  }
};


const getTypeIcon = (
  type: "flight" | "hotel",
  itineraryId: string,
  itineraryTypeMap: Map<string, Set<string>>
) => {
  const types = itineraryTypeMap.get(itineraryId);

  //Flight + Hotel (same itinerary)
  if (types?.has("flight") && types?.has("hotel")) {
    return <FlightHotelIcon sx={{ 
                                color: "#4f46e5",
                                mr: 0.5,
                                fontSize: "28px",
                              backgroundColor: "#dbeafe",
          borderRadius: "4px",
          padding: "5px",
                                }} />;
  }

  //Flight only
  if (type === "flight") {
    return (
      <Flight
        sx={{
          mr: 0.5,
          fontSize: "28px",
          color: "#1976d2",
          backgroundColor: "#dbeafe",
          borderRadius: "4px",
          padding: "5px",
        }}
      />
    );
  }

  //Hotel only
  return (
    <Hotel
      sx={{
        mr: 0.5,
        fontSize: "28px",
        color: "#7e22ce",
        backgroundColor: "#f3e8ff",
        borderRadius: "4px",
        padding: "5px",
      }}
    />
  );
};



// const getTypeIcon = (type: string) =>
//   type === "flight" ? (
//     <Flight sx={{ mr: 0.5, fontSize: "28px", color: "#1976d2", backgroundColor: "#dbeafe", borderRadius: "4px", padding: "5px" }} />
//   ) : (
//     <Hotel sx={{ mr: 0.5, fontSize: "28px", color: "#7e22ce", backgroundColor: "#f3e8ff", borderRadius: "4px", padding: "5px" }} />
//   );

  const mapFlightToRow = (apiResponse: any): ApiTransaction => {
  const firstPassenger = apiResponse.data.passengerDetails.passengers[0];
const outboundReturn =
  apiResponse.data.flightDetails[0]?.outboundReturn ?? "";

const [start, end] = outboundReturn.split(" - ");
  return {
    productType: "flight",
    itineraryId: "AUTO_FROM_PARENT", // comes from list API
    supplierName: apiResponse.data.flightDetails[0].airlineName,
    name: firstPassenger.name,
    bkdate: new Date().toISOString(),
    startDate: start || "",
    endDate: end || "",
    paymentStatus: firstPassenger.status,
    price: apiResponse.data.financialDetails.customerAmount,
    supplierSource: "",
    sup_conf_Id: "",
  };
};

const mapHotelToRow = (apiResponse: any): ApiTransaction => {
  const hotel = apiResponse.data.hotelDetails[0];
  const guest = apiResponse.data.passengerDetails.passengers[0];

  return {
    productType: "hotel",
    itineraryId: hotel.hotelId,
    supplierName: apiResponse.data.bookingInformation.supplierName,
    name: guest.name,
    bkdate: hotel.checkInDate,
    startDate: hotel.checkInDate,
    endDate: hotel.checkOutDate,
    paymentStatus: "Pending",
    price: apiResponse.data.financialDetails.customerAmount,
    supplierSource: "",
    sup_conf_Id: "",
  };
};



// type TxStatus = "Confirmed" | "On Hold" | "Pending" | "Cancelled" | string;

interface BookingDetails {
  bookingType?: string;
  pnr?: string;
  ticketNumber?: string;
  invoiceNumber?: string;
  voucherNumber?: string;
  supplierName?: string;
  supplierID?: string;
}

interface FlightHotelDetails {
  airlineName?: string;
  operatingAirline?: string;
  sector?: string;
  cabinClass?: string;
  refundType?: string;
  flightNumber?: string;
  flightTime?: string;
  hotelName?: string;
  roomType?: string;
  checkInDate?: string;
  checkOutDate?: string;
  numberOfNights?: number;
  numberOfRooms?: number;
  hotelAddress?: string;
}

interface TimelineHistory {
  createdBy?: string;
  createdByName?: string;
  lastModifiedBy?: string;
  lastModifiedByName?: string;
  assignedTo?: string;
}

interface PassengerDetails {
  type?: string;
  name?: string;
  customerContact?: string;
  customerEmail?: string;
  specialRequests?: string;
}

interface FinancialDetails {
  supplierName?: string;
  supplierAmount?: string;
  customerAmount?: string;
  paymentMethod?: string;
  dueDate?: string;
  profitMarkup?: string;
  discountAmount?: string;
  totalAmount?: string;
}

interface TxStatus {
  text: "Confirmed" | "Pending" | "On Hold" | "Cancelled";
}

interface Transaction {
  id: number;
  type: { text: string; icon: React.ReactNode };
  supplierName: string;
  itineraryId: string;
  customer: string;
  destination: string;
  outboundReturn: string;
  documentDate: string;
  amount: string;
  payment?: string;
  status: TxStatus;
  details?: BookingDetails;
  fhd?: FlightHotelDetails;
  timelineHistory?: TimelineHistory;
  passengerDetails?: PassengerDetails;
  financialDetails?: FinancialDetails;
}

interface Filters {
  type: string;
  supplierName: string;
  status: number | string;
  search: string;
}



export default function TransactionManagementSystem() {
  const LIMIT = 30;
  const [rows, setRows] = useState<ApiTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const offsetRef = useRef(0);
  const nextTokenRef = useRef<string | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = useState("");
  // List refresh: manual button always; auto-refresh is OPT-IN (off by default,
  // never a silent interval) and pauses while a workflow takeover is open.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [autoRefreshSec, setAutoRefreshSec] = useState(30); // user-chosen cadence
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [detailsMap, setDetailsMap] = useState<Record<string, any>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const TABLE_WIDTH = 2000;

const tableScrollRef = useRef<HTMLDivElement>(null);
const bottomScrollRef = useRef<HTMLDivElement>(null);
const [showRightShadow, setShowRightShadow] = React.useState(true);
const [openRowId, setOpenRowId] = useState<string | null>(null);

const [openRowKey, setOpenRowKey] = useState<string | null>(null);
interface ModifyContext {
  modifyTransactionId: string;
  itineraryId: string;
  customerName?: string;
  customerEmail?: string;
}
const [modifyContext, setModifyContext] = useState<ModifyContext | null>(null);
interface CancelContext {
  // Always the customer-raised cancel row to process; ops never initiates.
  cancelTransactionId: string;
  itineraryId: string;
  customerName?: string;
  customerEmail?: string;
}
const [cancelContext, setCancelContext] = useState<CancelContext | null>(null);
const lastScrollTopRef = useRef(0);

const getRowKey = (row: ApiTransaction) =>
  `${row.itineraryId}-${row.productType}`;


  
    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
};

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
};
  // Tracks which rows are open
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});

  const [filters, setFilters] = useState<Filters>({
    type: "All Types",
    supplierName: "All Supplier Names",
    status: "All Status",
    search: "",
  });

  const TAB_LABELS = ["All", "Failed", "Inprogress", "Modify", "Cancel", "Refund", "Confirmed", "Pending"];
  const [statusFilterType, setStatusFilterType] = useState<"PAYMENT STATUS" | "BOOKING STATUS">("PAYMENT STATUS");
  const [activeTab, setActiveTab] = useState("All");

  const toggleRow = (id: number) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Confirmed":
        return { textColor: "#059669", bgColor: "#d1fae5", borderColor: "#a7f3d0" };
      case "On Hold":
        return { textColor: "#c24153", bgColor: "#ffedd5", borderColor: "#fdba74" };
      case "Pending":
        return { textColor: "#d97706", bgColor: "#fef3c7", borderColor: "#fde68a" };
      case "Cancelled":
        return { textColor: "#dc2626", bgColor: "#fee2e2", borderColor: "#fecaca" };
      default:
        return { textColor: "inherit", bgColor: "inherit", borderColor: "inherit" };
    }
  };

const getTripTypeLabel = (tripType: string | number | undefined) => {
  switch (String(tripType)) {
    case "1":
      return "One Way";
    case "2":
      return "Round Trip";
    case "3":
      return "Multi City";
    default:
      return "-";
  }
};

const getTripTypeDisplay = (row: any) => {
  // Hotel logic
  if (row.productType === "hotel") {
    if (Number(row.triptype) === 1) return "City";
    if (Number(row.triptype) === 3) return "Multicity";
    return "-";
  }

  // Flight or others (existing logic)
  return getTripTypeLabel(row.tripType);
};

const normalizeStatus = (status?: string) => {
  if (!status) return "";

  const value = status.toLowerCase();

  if (["success", "succeeded", "paid", "confirmed"].includes(value)) {
    return "confirmed";
  }

  if (["pending", "inprogress", "processing"].includes(value)) {
    return "pending";
  }

  if (["on hold", "hold", "onhold"].includes(value)) {
    return "on hold";
  }

  if (["cancelled", "canceled", "failed", "rejected"].includes(value)) {
    return "cancelled";
  }

  return value;
};

const matchesTab = (tab: string, rawStatus?: string): boolean => {
  if (tab === "All") return true;
  if (!rawStatus) return false;
  const s = rawStatus.toLowerCase();
  switch (tab) {
    case "Failed":     return ["failed", "cancelled", "canceled", "rejected"].some(v => s.includes(v));
    case "Inprogress": return ["inprogress", "in progress", "processing"].some(v => s.includes(v));
    case "Modify":     return s.includes("modify") || s.includes("modified");
    case "Confirmed":  return ["confirmed", "success", "succeeded", "paid"].some(v => s.includes(v));
    case "Pending":    return ["pending", "on hold", "onhold"].some(v => s.includes(v));
    default:           return false;
  }
};

const itineraryTypeMap = React.useMemo(() => {
  const map = new Map<string, Set<string>>();

  rows.forEach((row) => {
    if (!map.has(row.itineraryId)) {
      map.set(row.itineraryId, new Set());
    }
    map.get(row.itineraryId)!.add(row.productType);
  });

  return map;
}, [rows]);



 const filteredRows = rows.filter((row) => {
  // TYPE filter
  if (filters.type !== "All Types") {
    if (filters.type.toLowerCase() !== row.productType) {
      return false;
    }
  }

  // SUPPLIER filter
  if (filters.supplierName !== "All Supplier Names") {
    if (
      row.supplierName.toLowerCase() !==
      filters.supplierName.toLowerCase()
    ) {
      return false;
    }
  }

  // STATUS filter
  if (filters.status !== "All Status") {
  const rowStatus = normalizeStatus(row.paymentStatus);
  const filterStatus = normalizeStatus(String(filters.status));

  if (rowStatus !== filterStatus) {
    return false;
  }
}

 // SEARCH filter (name or itinerary, with/without hyphen)
if (filters.search.trim()) {
  const rawSearch = filters.search.toLowerCase();

  // normalize search (remove hyphen & spaces)
  const normalizedSearch = rawSearch.replace(/[-\s]/g, "");

  const nameValue = row.name?.toLowerCase() ?? "";
  const itineraryValue = row.itineraryId?.toLowerCase() ?? "";

  // normalize itinerary
  const normalizedItinerary = itineraryValue.replace(/[-\s]/g, "");

  if (
    !nameValue.includes(rawSearch) &&
    !normalizedItinerary.includes(normalizedSearch)
  ) {
    return false;
  }
}

  // TAB filter - Modify / Refund use the SMB-XXXX reqType enrichment;
  // other tabs fall back to the legacy status normalization.
  if (activeTab === "Modify") {
    if (row.reqType !== "modify") return false;
  } else if (activeTab === "Refund") {
    if (row.reqType !== "refund") return false;
  } else if (activeTab === "Cancel") {
    if (row.reqType !== "cancel") return false;
  } else {
    const tabStatusValue = statusFilterType === "PAYMENT STATUS" ? row.paymentStatus : row.bkStatus;
    if (!matchesTab(activeTab, tabStatusValue)) return false;
  }

  return true;
});

const supplierOptions = React.useMemo(() => {
  const uniqueSuppliers = Array.from(
    new Set(rows.map(row => row.supplierName))
  );
  return uniqueSuppliers;
}, [rows]);

const getRowUniqueKey = (row: ApiTransaction) => {
  return [
    row.productType,
    row.itineraryId,
    row.name,
    row.startDate,
    row.endDate,
    row.price,
    row.supplierName,
    row.paymentStatus,
  ]
    .map(v => String(v ?? "").trim().toLowerCase())
    .join("|");
};


  const fetchTransactions = async (currentOffset: number) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const paginationBody: Record<string, unknown> = { offset: currentOffset, count: (currentOffset + 1) * LIMIT };
      if (nextTokenRef.current) paginationBody.nextToken = nextTokenRef.current;

      const response = await getTransactionsAPI({ pagination: paginationBody });

      // Handle both response shapes:
      // { data: [...] }  OR  { data: { data: [...], pagination: { nextToken } } }
      const responseData = response?.data;
      const rawRows: ApiTransaction[] = Array.isArray(responseData)
        ? responseData
        : (Array.isArray(responseData?.data) ? responseData.data : []);
      const newRows: ApiTransaction[] = rawRows.map(normalizeEnrichment);

      const pagination = Array.isArray(responseData) ? null : responseData?.pagination;
      const token = pagination?.nextToken ?? null;
      nextTokenRef.current = token;

      if (newRows.length === 0) {
        hasMoreRef.current = false;
        setHasMore(false);
      }

      setRows(prev => {
        const map = new Map<string, ApiTransaction>();
        [...prev, ...newRows].forEach(row => {
          const key = getRowUniqueKey(row);
          if (!map.has(key)) map.set(key, row);
        });
        return Array.from(map.values());
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // Reset pagination and re-fetch from the top. Used on mount, the manual Refresh
  // button, the opt-in auto-refresh tick, and on returning from a workflow takeover
  // (so the list reflects the modify/cancel action without a hard page reload).
  const reloadTransactions = () => {
    loadingRef.current = false;
    hasMoreRef.current = true;
    offsetRef.current = 0;
    nextTokenRef.current = null;
    setOffset(0);
    setHasMore(true);
    setRows([]);
    fetchTransactions(0);
  };

  useEffect(() => {
    reloadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opt-in auto-refresh: only when the user enabled it AND no workflow takeover is
  // open (don't poll a list nobody is looking at). Cleared on toggle-off / unmount.
  useEffect(() => {
    if (!autoRefresh || modifyContext || cancelContext) return;
    const id = window.setInterval(() => reloadTransactions(), autoRefreshSec * 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, autoRefreshSec, modifyContext, cancelContext]);




// const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
//   const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

  
//   if (scrollHeight - scrollTop - clientHeight < 100) {
//     if (!loading && hasMore) {
//       const nextOffset = offset + 1;
//       setOffset(nextOffset);
//       fetchTransactions(nextOffset);
//     }
//   }
// };

// const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
//   const target = e.currentTarget;
//   if (target.scrollTop === 0) return;

//   const reachedBottom =
//     target.scrollTop + target.clientHeight >= target.scrollHeight - 50;

//   if (reachedBottom && !loading && hasMore) {
//     setOffset(prev => {
//       const next = prev + 1;
//       fetchTransactions(next);
//       return next;
//     });
//   }
// };

const syncFromTable = () => {
  if (!tableScrollRef.current || !bottomScrollRef.current) return;
  bottomScrollRef.current.scrollLeft =
    tableScrollRef.current.scrollLeft;
};

const syncFromBottom = () => {
  if (!tableScrollRef.current || !bottomScrollRef.current) return;
  tableScrollRef.current.scrollLeft =
    bottomScrollRef.current.scrollLeft;
};

// const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
//   const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

//   if (scrollHeight - scrollTop - clientHeight < 100) {
//     if (!loading && hasMore) {
//       setOffset(prev => {
//         const next = prev + 1;
//         fetchTransactions(next);
//         return next;
//       });
//     }
//   }
// };

// const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
//   if (openRowKey) return;
//   const target = e.currentTarget;
//   const { scrollTop, scrollHeight, clientHeight } = target;

//   const isScrollingDown = scrollTop > lastScrollTopRef.current;
//   lastScrollTopRef.current = scrollTop;

//   if (!isScrollingDown) return; 

//   const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

//   if (distanceFromBottom < 120 && !loading && hasMore) {
//     setOffset(prev => {
//       const next = prev + 1;
//       fetchTransactions(next);
//       return next;
//     });
//   }
// };

const loadMore = () => {
  if (loadingRef.current || !hasMoreRef.current) return;

  const nextOffset = offsetRef.current + 1;
  offsetRef.current = nextOffset;
  setOffset(nextOffset);
  fetchTransactions(nextOffset);
};

const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
  if (openRowKey) return;
  const target = e.currentTarget;
  const { scrollTop, scrollHeight, clientHeight } = target;

  const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

  if (distanceFromBottom <= 90) {
    loadMore();
  }
};



const stickyRightBase = {
  position: "sticky",
  backgroundColor: "#ffffff",
  zIndex: 5,
  whiteSpace: "nowrap",
};

const stickyHeaderBase = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: "#f1f3f4",
};



    
 

  // const capitalize = (value?: string) => {
  // if (!value) return "-";
  // return value
  //   .toLowerCase()
  //   .split(" ")
  //   .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  //   .join(" ");
  // };

  const capitalize = (value?: string) => {
  if (!value) return "-";

  const trimmed = value.trim();

  //If value looks like an email → keep lowercase
  if (trimmed.includes("@")) {
    return trimmed.toLowerCase();
  }

  //Normal text → Capitalize words
  return trimmed
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};


  const formatDate = (date?: string) => {
  if (!date) return "-";

  const parsed = new Date(date);

  if (isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};



  const addHyphenAfterThree = (value?: string) => {
    if (!value) return "-";
    return value.match(/.{1,3}/g)?.join("-") ?? value;
  };

  const mapFinancialDetails = (data: any, supplierNameFromRow?: string) => {

  const fd = data?.financialDetails ?? {};

  const markupAmount = Number(fd?.profitOrMarkup?.markup?.amount ?? 0);
  const serviceFeeAmount = Number(fd?.profitOrMarkup?.serviceFee?.amount ?? 0);

  return {
    supplierName: supplierNameFromRow ?? "-",

    supplierAmount: fd?.supplierAmount ?? "-",
    customerAmount: fd?.customerAmount ?? "-",
    tax: fd?.tax ?? "",
    profitMarkup:
      markupAmount + serviceFeeAmount > 0
        ? `${markupAmount + serviceFeeAmount}`
        : "-",

    discountAmount:
  fd?.discountAmount !== undefined && fd?.discountAmount !== null
    ? fd.discountAmount
    : "-",

    paymentType: fd?.paymentType ?? "-",
    totalAmount: fd?.totalAmount ?? "-",
  };
};


  const buildPayload = (row: ApiTransaction) => {
  if (row.productType === "flight") {
    return {
      productType: "flight",
      itineraryId: row.itineraryId,
      name: row.name,
      paymentStatus: row.paymentStatus,
      price: row.price,
      supplierSource: row.supplierSource,          // adjust if dynamic
      supplierName: row.supplierName,
      tripType: row.tripType ?? "1",                  // 1 = One way, 2 = Round trip
    };
  }

  // HOTEL
  return {
    productType: "hotel",
    itineraryId: row.itineraryId,
    name: row.name,
    bkdate: row.bkdate,
    paymentStatus: row.paymentStatus,
    price: row.price,
    startDate: row.startDate,
    endDate: row.endDate,
    supplierSource: row.supplierSource,
    supplierName: row.supplierName,
  };
};

const handleRowClick = async (row: ApiTransaction, rowIndex: number) => {
  const rowKey = getRowKey(row);

  // toggle close
  if (openRowKey === rowKey) {
    setOpenRowKey(null);
    return;
  }

  setOpenRowKey(rowKey);

  // Scroll clicked row to top
  setTimeout(() => {
    const rowElement = document.getElementById(`transaction-row-${rowIndex}`);
    if (rowElement && tableScrollRef.current) {
      const containerTop = tableScrollRef.current.getBoundingClientRect().top;
      const rowTop = rowElement.getBoundingClientRect().top;
      const offset = rowTop - containerTop;
      tableScrollRef.current.scrollBy({ top: offset - 10, behavior: 'smooth' });
    }
  }, 100);

  // already loaded → don’t call API again
  if (detailsMap[rowKey]) return;
  const payload = buildPayload(row);

  console.log("Payload Sent:", payload);

  // OPTIONAL: send to backend
  try {
    setDetailsLoading(true);
    const res = await getTransactionDetailsAPI(payload);
    const mappedFinancial = mapFinancialDetails(res?.data, row.supplierName);
    console.log("Financial UI:", mappedFinancial);
    setDetailsMap(prev => ({
      ...prev,
      [rowKey]: {
    ...res?.data,
    financialUI: mappedFinancial,
  },
    }));
  } catch (error) {
    console.error("Failed to send payload", error);
  } finally {
    setDetailsLoading(false);
  }
};

const FlightDetails = ({ data }: { data: any }) => {
  const routeSupplierMap = React.useMemo(() => {
  const map = new Map<string, string>();

  (data?.flightDetails || []).forEach((f: any) => {
    if (f.route && f.supplierName) {
      map.set(
        f.route.trim().toLowerCase(),
        f.supplierName
      );
    }
  });

  return map;
}, [data?.flightDetails]);

  if (!data || !Array.isArray(data.flightDetails)) {
    return <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>No flight details available</Typography>;
  }
  const flight = data.flightDetails;
  const pax = data.passengerDetails?.passengers ?? [];

  const passengersByRoute = pax.reduce((acc: Record<string, any[]>, passenger: any) => {
  const route = passenger.route || "Unknown Route";

  if (!acc[route]) {
    acc[route] = [];
  }

  acc[route].push(passenger);
  return acc;
}, {});




const flowTypeValue = data.bookingInformation?.flowType
  ? capitalize(data.bookingInformation.flowType)
  : "";

  
const formatStatus = (value?: string) => {
  if (!value) return "-";

  return value
    .toString()
    .trim()
    .toLowerCase()
    // Fix: onhold / on-hold / on_hold → on hold
    .replace(/on[\s_-]*hold/g, "on hold")
    // Remove extra spaces
    .replace(/\s+/g, " ")
    // Capitalize words
    .replace(/\b\w/g, char => char.toUpperCase());
};





  return (
    <>
    <Box className="transactionCardWrap">
        <Typography variant="subtitle1" sx={{fontWeight: "600", marginBottom: "15px", display: "flex", alignItems: "center", gap: 1}}>
          <Flight sx={{ mr: 0.5, fontSize: "28px", color: "#1976d2", backgroundColor: "#dbeafe", borderRadius: "4px", padding: "5px" }} /> Flight Details
        </Typography>
        <Box className="flightTitleRow flightCommonTrip" sx={{width: "85%", borderBottom: "1px solid #e0e0e0", paddingBottom: "5px", backgroundColor: "#fafafa"}}>
              <Typography variant="body2" component="div">
                <div className="transactionCardTitle splitType">Type</div>
              </Typography>
              <Typography variant="body2" component="div">
                <div className="transactionCardTitle airlineName">Airline Name</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle operatingAirline">Operating Airline</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle date">Date</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle route">Sector / Route</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle supplierName">Supplier</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle cabinClass">Cabin Class</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle flightNumber">Flight Number</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle flightTime">Flight Time</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle refundType">Refund Type</div>
              </Typography>

              <Typography variant="body2" component="div">
                <div className="transactionCardTitle sectorStatus">Sector Status</div>
              </Typography>
            </Box>
            {flight.map((flights: any, i: number) => (
            <Box className="flightCommonTrip" key={i} sx={{ mb: 2 }}>
              
<Tooltip
  title={flowTypeValue}
  disableHoverListener={!flowTypeValue}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue splitType">
                    {flowTypeValue || "-"}
                  </strong>
                </Typography>
                </Tooltip>
             <Tooltip
  title={flights.airlineName ? flights.airlineName : ""}
  disableHoverListener={!flights.airlineName}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue airlineName">{flights.airlineName ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.operatingAirline ? flights.operatingAirline : ""}
  disableHoverListener={!flights.operatingAirline}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue operatingAirline">{flights.operatingAirline ?? "-"}</strong>
                </Typography>
            </Tooltip>
             <Tooltip
  title={flights.outboundReturn ? flights.outboundReturn : ""}
  disableHoverListener={!flights.outboundReturn}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue outboundReturn">
                    {flights.outboundReturn ?? "-"}
                  </strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.route ? flights.route : ""}
  disableHoverListener={!flights.route}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue route">{flights.route ?? "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={capitalize(flights.supplierName) ? capitalize(flights.supplierName) : ""}
  disableHoverListener={!capitalize(flights.supplierName)}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue supplierName">{capitalize(flights.supplierName) ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.cabinClass ? flights.cabinClass : ""}
  disableHoverListener={!flights.cabinClass}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue cabinClass">{flights.cabinClass ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.flightNumber ? flights.flightNumber : ""}
  disableHoverListener={!flights.flightNumber}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightNumber">{flights.flightNum ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.flightTime ? flights.flightTime : ""}
  disableHoverListener={!flights.flightTime}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightTime">{flights.flightTime ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={flights.refundType ? flights.refundType : ""}
  disableHoverListener={!flights.refundType}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue refundType">{flights.refundType ?? "-"}</strong>
                </Typography>
                </Tooltip>
<Tooltip
  title={capitalize(flights.sectorStatus) ? capitalize(flights.sectorStatus) : ""}
  disableHoverListener={!capitalize(flights.sectorStatus)}
  arrow
  placement="top"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue sectorStatus">{capitalize(flights.sectorStatus) ?? "-"}</strong>
                </Typography>
                </Tooltip>
              </Box>
              ))}
    </Box>
      <Box className="transactionCardWrap">
         <Typography variant="subtitle1" sx={{fontWeight: "600", marginBottom: "15px", display: "flex", alignItems: "center", gap: 1}}>
    <PersonOutlineIcon />  
    Passenger Details
  </Typography>
  {(Object.entries(passengersByRoute) as [string, any[]][]).map(([route, passengers]) => {
    const supplier =
      route
        ? routeSupplierMap.get(route.trim().toLowerCase())
        : null;
  
  return (
    
    <Box key={route} sx={{ mb: 3 }}>
  <Typography variant="body2" sx={{marginBottom: '15px'}}>
                
                <strong className="transactionCardValue noWidth">{route ?? "-"}  <span style={{ marginLeft: 8, fontSize: 14, color: "#4f46e5" }}>
  ({supplier ? capitalize(supplier) : "-"})
</span></strong>
               
              </Typography>
       <Box className="flightTitleRow flightCommonTrip" sx={{width: "85%", borderBottom: "1px solid #e0e0e0", paddingBottom: "5px", backgroundColor: "#fafafa"}}>
            <Typography variant="body2">
                <div className="transactionCardTitle flightType">Type</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightName">Name</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightPnr">PNR / Confirmation</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightTicketNumber">Ticket Number</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightEmail">Email</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightSeat">Seat</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightMeal">Meal</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightBaggage">Baggage</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle flightStatus">Status</div>
              </Typography>
          </Box>

          {passengers.map((p: any, i: number) => (
            <Box className="flightCommonTrip" key={i}>
              <Tooltip
  title={p.type ? p.type : ""}
  disableHoverListener={!p.type}
  arrow
  placement="bottom"
>
              <Typography variant="body2">
                  <strong className="transactionCardValue flightType">{p.type ?? "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.name ? p.name : ""}
  disableHoverListener={!p.name}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightName">{capitalize(p.name) || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.pnr ? p.pnr : ""}
  disableHoverListener={!p.pnr}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightPnr">{p.pnr || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.ticketNumber ? p.ticketNumber : ""}
  disableHoverListener={!p.ticketNumber}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightTicketNumber">{p.ticketNumber || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={capitalize(p.paxEmail) ? capitalize(p.paxEmail) : ""}
  disableHoverListener={!capitalize(p.paxEmail)}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightEmail">{capitalize(p.paxEmail) || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.seat ? p.seat : ""}
  disableHoverListener={!p.seat}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightSeat">{p.seat || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.meal ? p.meal : ""}
  disableHoverListener={!p.meal}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightMeal">{p.meal || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.baggage ? p.baggage : ""}
  disableHoverListener={!p.baggage}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightBaggage">{p.baggage || "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={formatStatus(p.ticketingStatus) !== "-" 
    ? formatStatus(p.ticketingStatus) 
    : ""}
  disableHoverListener={formatStatus(p.ticketingStatus) === "-"}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue flightStatus">{formatStatus(p.ticketingStatus)}</strong>
                </Typography>
                </Tooltip>
            </Box>
          ))}
      </Box>
      );
    }
    )}
      <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3,1fr)" },
                gap: 25,
                marginTop: "15px",
                borderTop: "1px solid #ccc",
                paddingTop: "10px",
              }}
            >
            <Typography variant="body2">
              <div className="transactionCardTitle customerContact">Customer Contact</div>
              <strong className="transactionCardValue customerContact">{data.passengerDetails?.customerContact ?? "-"}</strong>
            </Typography>
      
            <Typography variant="body2">
              <div className="transactionCardTitle customerEmail">Customer Email</div>
              <strong className="transactionCardValue customerEmail">{capitalize(data.passengerDetails?.customerEmail) ?? "-"}</strong>
            </Typography>
        </Box>
      </Box>
      <Box className="transactionCardWrap">
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: "600",
      marginBottom: "15px",
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    <AttachMoneyIcon /> Financial Details
  </Typography>

  {!data.financialUI ? (
    <Typography variant="body2">No financial details available</Typography>
  ) : (
    <Box className="financialWrapper">
      <Box>
        <Typography className="financialWrap">
          <div className="transactionCardTitle">Supplier Name</div>
          <strong className="transactionCardValue supplierNameFinancial">
            {formatStatus(data.financialUI.supplierName)}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Supplier Amount</div>
          <strong className="transactionCardValue supplierAmount">
            {data.financialUI.supplierAmount}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Profit / Markup</div>
          <strong className="transactionCardValue profitMarkup">
            {data.financialUI.profitMarkup}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Customer Amount</div>
          <strong className="transactionCardValue customerAmount">
            {data.financialUI.customerAmount}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Discount Amount</div>
          <strong className="transactionCardValue discountAmount">
            {data.financialUI.discountAmount
  ? Number(data.financialUI.discountAmount).toFixed(2)
  : "0.00"}
          </strong>
        </Typography>
      </Box>

      <Box>
        <Typography className="financialWrap">
          <div className="transactionCardTitle">Payment Method</div>
          <strong className="transactionCardValue paymentMethod">
            {capitalize(data.financialUI.paymentType)}
          </strong>
        </Typography>

        <Typography className="financialWrap profit">
          <div className="transactionCardTitle">Total Amount</div>
          <strong className="transactionCardValue totalAmount">
            {Number(data.financialUI.totalAmount).toFixed(2)}
          </strong>
        </Typography>
      </Box>
    </Box>
  )}
</Box>


    </>
  );
};

const HotelDetails = ({ data }: { data: any }) => {
  if (!data || !Array.isArray(data.hotelDetails) || data.hotelDetails.length === 0) {
    return <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>No hotel details available</Typography>;
  }
  const hotel = data.hotelDetails;
  const pax = data.passengerDetails?.passengers ?? [];

  const passengersByHotel = pax.reduce(
  (acc: Record<string, any[]>, passenger: any) => {
    const hotelName = passenger.hotelName || "Unknown Hotel";

    if (!acc[hotelName]) {
      acc[hotelName] = [];
    }

    acc[hotelName].push(passenger);
    return acc;
  },
  {}
);


  const cellStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const HOTEL_SUPPLIER_NAME_MAP: Record<string, string> = {
  hoteltrader: "Hotel Trader",
};

 const hotelFormatStatus = (value?: string) => {
  if (!value) return "-";

  const cleaned = value.toString().trim().toLowerCase();

  //1. Exact supplier mapping (handles HOTELTRADER)
  if (HOTEL_SUPPLIER_NAME_MAP[cleaned]) {
    return HOTEL_SUPPLIER_NAME_MAP[cleaned];
  }

  return value
    .toString()
    .trim()

    //Replace underscores & hyphens with space
    .replace(/[_-]+/g, " ")

    //Insert space before capital letters (HotelTrader → Hotel Trader)
    .replace(/([a-z])([A-Z])/g, "$1 $2")

    //Normalize spaces
    .replace(/\s+/g, " ")

    //Lowercase everything
    .toLowerCase()

    //Capitalize each word
    .replace(/\b\w/g, char => char.toUpperCase());
};




  return (
    <>
    <Box className="transactionCardWrap">
        <Typography variant="subtitle1" sx={{fontWeight: "600", marginBottom: "15px", display: "flex", alignItems: "center", gap: 1}}>
          <Hotel sx={{ mr: 0.5, fontSize: "28px", color: "#7e22ce", backgroundColor: "#f3e8ff", borderRadius: "4px", padding: "5px" }} /> Hotel Details
        </Typography>
        <Box className="flightTitleRow flightCommonTrip" sx={{width: "85%", borderBottom: "1px solid #e0e0e0", paddingBottom: "5px", backgroundColor: "#fafafa"}}>
              <Typography variant="body2">
                <div className="transactionCardTitle hotelName">Hotel Name</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle hotelAddress">Hotel Address</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle roomType">Room Type</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle mealType">Board Basis</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle checkInDate">Check-in Date</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle checkOutDate">Check-out Date</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle numberOfNights">Number of Nights</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle numberOfRooms">Number of Rooms</div>
              </Typography>
        
              <Typography variant="body2">
                <div className="transactionCardTitle refundType">Refund Type</div>
              </Typography>

              <Typography variant="body2">
                <div className="transactionCardTitle cityStatus">Status</div>
              </Typography>
            </Box>
            {hotel.map((hotels: any, i: number) => {
            const nightsLabel =
  hotels.nights
    ? `${hotels.nights} ${hotels.nights === 1 ? "Night" : "Nights"}`
    : "-";
    const roomsLabel =
  hotels.numRooms
    ? `${hotels.numRooms} ${hotels.numRooms === 1 ? "Room" : "Rooms"}`
    : "-";

           return (
              
            <Box className="flightCommonTrip" key={i} sx={{ mb: 2 }}>
              <Tooltip
  title={capitalize(hotels.hotelName) ? capitalize(hotels.hotelName) : ""}
  disableHoverListener={!hotels.hotelName}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue hotelName">{capitalize(hotels.hotelName) ?? "-"}</strong>
                </Typography>
            </Tooltip>
            <Tooltip
  title={capitalize(hotels.hotelAddress) ? capitalize(hotels.hotelAddress) : ""}
  disableHoverListener={!hotels.hotelAddress}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue hotelAddress">{capitalize(hotels.hotelAddress) ?? "-"}</strong>
                </Typography>
            </Tooltip>
            <Tooltip
  title={capitalize(hotels.roomType) ? capitalize(hotels.roomType) : ""}
  disableHoverListener={!capitalize(hotels.roomType)}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue roomType">
                    {capitalize(hotels.roomType) ?? "-"}
                  </strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={capitalize(hotels.meal) ? capitalize(hotels.meal) : ""}
  disableHoverListener={!hotels.meal}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue mealType">{capitalize(hotels.meal) ?? "-"}</strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={hotels.checkInDate ? formatDate(hotels.checkInDate) : ""}
  disableHoverListener={!hotels.checkInDate}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue checkInDate">
                    {hotels.checkInDate ? formatDate(hotels.checkInDate) : "-"}
                  </strong>
                </Typography>
                </Tooltip>
<Tooltip
  title={hotels.checkOutDate ? formatDate(hotels.checkOutDate) : ""}
  disableHoverListener={!hotels.checkOutDate}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue checkOutDate">
                    {hotels.checkOutDate ? formatDate(hotels.checkOutDate) : "-"}
                  </strong>
                </Typography>
                </Tooltip>
            <Tooltip
  title={nightsLabel !== "-" ? nightsLabel : ""}
  disableHoverListener={nightsLabel === "-"}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue numberOfNights">{nightsLabel}</strong>
                </Typography>
                </Tooltip>
<Tooltip
  title={roomsLabel !== "-" ? roomsLabel : ""}
  disableHoverListener={roomsLabel === "-"}
  arrow
>
               <Typography variant="body2">
                  <strong className="transactionCardValue numberOfRooms">
                    {roomsLabel}
                  </strong>
                </Typography>
                </Tooltip>
<Tooltip
  title={hotels.refundType ? hotels.refundType : "-"}
  disableHoverListener={!hotels.refundType}
  arrow
  placement="bottom"
>
            
                <Typography variant="body2">
                  <strong className="transactionCardValue refundType">{hotels.refundType ?? "-"}</strong>
                </Typography>
                </Tooltip>
<Tooltip
  title={hotels.cityStatus ? hotels.cityStatus : "-"}
  disableHoverListener={!hotels.cityStatus}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue cityStatus">{hotels.cityStatus ?? "-"}</strong>
                </Typography>
                </Tooltip>
              </Box>
              );
}
)}
    </Box>
     

      <Box className="transactionCardWrap">
         <Typography variant="subtitle1" sx={{fontWeight: "600", marginBottom: "15px", display: "flex", alignItems: "center", gap: 1}}>
    <PersonOutlineIcon />  
    Guest Details
  </Typography>
  {(Object.entries(passengersByHotel) as [string, any[]][]).map(
    ([hotelName, passengers]) => (
      <Box key={hotelName} sx={{ mb: 3 }}>
  <Typography variant="body2" sx={{ marginTop: "35px", marginBottom: "-5px", textTransform: "uppercase" }}>
          <strong className="transactionCardValue noWidth">
            {hotelName || "-"}
          </strong>
        </Typography>
          <Box className="flightTitleRow flightCommonTrip" sx={{width: "35%", borderBottom: "1px solid #e0e0e0", paddingBottom: "5px", backgroundColor: "#fafafa"}}>
            <Typography variant="body2">
                <div className="transactionCardTitle hotelType">Type</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle guestName">Name</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle hotelId">Hotel ID</div>
              </Typography>
              <Typography variant="body2">
                <div className="transactionCardTitle hotelEmail">Email</div>
              </Typography>
          </Box>
          {pax.map((p: any, i: number) => (
            <Box className="flightCommonTrip" key={i}>
              <Typography variant="body2">
                  <strong className="transactionCardValue hotelType">{p.type ?? "-"}</strong>
                </Typography>
                <Tooltip
  title={p.name || ""}
  disableHoverListener={!p.name}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue guestName">{p.name ?? "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={p.hotelId || ""}
  disableHoverListener={!p.hotelId}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue hotelId">{p.hotelId ?? "-"}</strong>
                </Typography>
                </Tooltip>
                <Tooltip
  title={capitalize(p.paxEmail) || ""}
  disableHoverListener={!capitalize(p.paxEmail)}
  arrow
  placement="bottom"
>
                <Typography variant="body2">
                  <strong className="transactionCardValue hotelEmail">{capitalize(p.paxEmail) ?? "-"}</strong>
                </Typography>
                </Tooltip>
            </Box>
          ))}
          </Box>
    )
  )}
       <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3,1fr)" },
                gap: 25,
                marginTop: "15px",
                borderTop: "1px solid #ccc",
                paddingTop: "10px",
              }}
            >
            <Typography variant="body2">
              <div className="transactionCardTitle customerContact">Customer Contact</div>
              <strong className="transactionCardValue customerContact">{data.passengerDetails?.customerContact ?? "-"}</strong>
            </Typography>
      
            <Typography variant="body2">
              <div className="transactionCardTitle customerEmail">Customer Email</div>
              <strong className="transactionCardValue customerEmail">{capitalize(data.passengerDetails?.customerEmail) ?? "-"}</strong>
            </Typography>
        </Box>
</Box>
     
       <Box className="transactionCardWrap">
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: "600",
      marginBottom: "15px",
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    <AttachMoneyIcon /> Financial Details
  </Typography>

  {!data.financialUI ? (
    <Typography variant="body2">No financial details available</Typography>
  ) : (
    <Box className="financialWrapper">
      <Box>
        <Typography className="financialWrap">
          <div className="transactionCardTitle">Supplier Name</div>
          <strong className="transactionCardValue supplierNameFinancial">
            {hotelFormatStatus(data.financialUI.supplierName)}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Supplier Amount (Base Fare + Tax)</div>
          <strong className="transactionCardValue supplierAmount">
      {(() => {
      const base = Number(data.financialUI.supplierAmount || 0);
      const tax = Number(data.financialUI.tax || 0);
      const total = base + tax;

      return total.toFixed(2);
    })()}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Profit / Markup</div>
          <strong className="transactionCardValue profitMarkup">
            {data.financialUI.profitMarkup}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Customer Amount</div>
          <strong className="transactionCardValue customerAmount">
            {data.financialUI.customerAmount}
          </strong>
        </Typography>

        <Typography className="financialWrap">
          <div className="transactionCardTitle">Discount Amount</div>
          <strong className="transactionCardValue discountAmount">
            {data.financialUI.discountAmount
  ? Number(data.financialUI.discountAmount).toFixed(2)
  : "0.00"}
          </strong>
        </Typography>
      </Box>

      <Box>
        <Typography className="financialWrap">
          <div className="transactionCardTitle">Payment Method</div>
          <strong className="transactionCardValue paymentMethod">
            {capitalize(data.financialUI.paymentType)}
          </strong>
        </Typography>

        <Typography className="financialWrap profit">
          <div className="transactionCardTitle">Total Amount</div>
          <strong className="transactionCardValue totalAmount">
            {Number(data.financialUI.totalAmount).toFixed(2)}
          </strong>
        </Typography>
      </Box>
    </Box>
  )}
</Box>
    </>
  );
};



  if (modifyContext) {
    return (
      <ModifyBookingWorkflow
        modifyTransactionId={modifyContext.modifyTransactionId}
        itineraryId={modifyContext.itineraryId}
        customerName={modifyContext.customerName}
        customerEmail={modifyContext.customerEmail}
        onBack={() => { setModifyContext(null); reloadTransactions(); }}
      />
    );
  }

  if (cancelContext) {
    return (
      <CancelBookingWorkflow
        cancelTransactionId={cancelContext.cancelTransactionId}
        itineraryId={cancelContext.itineraryId}
        customerName={cancelContext.customerName}
        customerEmail={cancelContext.customerEmail}
        onBack={() => { setCancelContext(null); reloadTransactions(); }}
      />
    );
  }

  return (
    <Box className="transactionList">
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Transaction Management System
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monitor and manage all flight and hotel booking transactions
      </Typography>

      {/* Summary cards row using css grid for simplicity */}
      {/* <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(6,1fr)" },
          gap: 2,
          mb: 5,
        }}
        className="statusShowBox"
      >
        <Card sx={{ bgcolor: "#ffffff" }}>
          <CardContent className="paddingBottom">
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#6b7280"}}>Total Transactions</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "24px", color: "#111827"}}>
              {transactionsMock.length}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#ffffff" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#6b7280"}}>Total Revenue</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "24px", color: "#16a34a"}}>
              $6,590.00
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#f0fdf4" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#15803d"}}>Confirmed</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#14532d" }}>
              1
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#fefce8" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#a16207"}}>On Hold</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#713f12"}}>
              1
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#eff6ff" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#1d4ed8"}}>Pending</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e3a8a" }}>
              1
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#fef2f2" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#b91c1c"}}>Cancelled</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#7f1d1d" }}>
              1
            </Typography>
          </CardContent>
        </Card>
      </Box> */}
 
      {/* Status Type Dropdown + Tabs */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 200, backgroundColor: "#fff" }}>
          <Select
            className="leftFilterBox"
            value={statusFilterType}
            onChange={(e) => {
              setStatusFilterType(e.target.value as "PAYMENT STATUS" | "BOOKING STATUS");
              setActiveTab("All");
            }}
          >
            <MenuItem value="PAYMENT STATUS">Payment Status</MenuItem>
            <MenuItem value="BOOKING STATUS">Booking Status</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {TAB_LABELS.map((tab) => (
            <Box
              key={tab}
              onClick={() => setActiveTab(tab)}
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: activeTab === tab ? 600 : 400,
                border: "1px solid",
                borderColor: activeTab === tab ? "#272727" : "#d1d5db",
                backgroundColor: activeTab === tab ? "#272727" : "#fff",
                color: activeTab === tab ? "#fff" : "#555",
                userSelect: "none",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "#272727",
                  backgroundColor: activeTab === tab ? "#272727" : "#f5f5f5",
                },
              }}
            >
              {tab}
            </Box>
          ))}
        </Box>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          {lastUpdated && (
            <Typography sx={{ fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
            onClick={reloadTransactions}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: "20px", color: "#272727", borderColor: "#d1d5db" }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant={autoRefresh ? "contained" : "outlined"}
            onClick={() => setAutoRefresh((v) => !v)}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              ...(autoRefresh
                ? { backgroundColor: "#272727", "&:hover": { backgroundColor: "#272727" } }
                : { color: "#272727", borderColor: "#d1d5db" }),
            }}
          >
            Auto-refresh {autoRefresh ? "on" : "off"}
          </Button>
          {autoRefresh && (
            <FormControl size="small">
              <Select
                value={autoRefreshSec}
                onChange={(e) => setAutoRefreshSec(Number(e.target.value))}
                sx={{ backgroundColor: "#fff", fontSize: "13px", borderRadius: "20px", height: 32 }}
              >
                <MenuItem value={10}>Every 10s</MenuItem>
                <MenuItem value={30}>Every 30s</MenuItem>
                <MenuItem value={60}>Every 1m</MenuItem>
                <MenuItem value={120}>Every 2m</MenuItem>
                <MenuItem value={300}>Every 5m</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Filters */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
        >
        <FilterAlt sx={{ color: "#6b7280" }} />
        <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
          {/* <InputLabel>Type</InputLabel> */}
          <Select
            className='leftFilterBox'
            value={filters.type}
            label="Type"
            onChange={(e) => handleFilterChange("type", String(e.target.value))}
          >
            <MenuItem value="All Types">All Types</MenuItem>
            <MenuItem value="Flight">Flight</MenuItem>
            <MenuItem value="Hotel">Hotel</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
          {/* <InputLabel>supplierName</InputLabel> */}
          <Select
            className='leftFilterBox'
            value={filters.supplierName}
            label="supplierName"
            onChange={(e) =>
              handleFilterChange("supplierName", String(e.target.value))
            }
          >
            <MenuItem value="All Supplier Names">All Supplier Names</MenuItem>
            {supplierOptions.map((supplier) => (
              <MenuItem key={supplier} value={supplier}>
                {capitalize(supplier)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
          {/* <InputLabel>Status</InputLabel> */}
          <Select
            className='leftFilterBox'
            value={filters.status}
            label="Status"
            onChange={(e) => handleFilterChange("status", String(e.target.value))}
          >
            <MenuItem value="All Status">All Status</MenuItem>
            <MenuItem value="Confirmed">Confirmed</MenuItem>
            <MenuItem value="On Hold">On Hold</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          // mb: 4,
        }}
      >
      
        <TextField
          placeholder="Search by name or itinerary"
          variant="outlined"
          size="small"
          sx={{ minWidth: 800, backgroundColor: "#fff" }}
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
          }}
        />

        
{/* <Box sx={{display: "flex", gap: 2}}>
        <Button variant="contained" startIcon={<Add />} sx={{background: "#272727"}}>
          New Transaction
          </Button>
        <Button variant="contained" startIcon={<FileDownload />} sx={{background: "#b72323"}}>
          Export
        </Button>
        
        </Box> */}
      </Box>
      </Box>

      {/* Table */}
      <Box
  sx={{
    height: "95vh",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
    borderRadius: 2,
    overflow: "hidden",
  }}
>
      <TableContainer
    ref={tableScrollRef}
    onScroll={(e) => {
      const target = e.currentTarget;
      handleScroll(e);   // vertical scroll (API)
      syncFromTable();   // horizontal sync
      const maxScrollLeft = target.scrollWidth - target.clientWidth;
      setShowRightShadow(target.scrollLeft < maxScrollLeft - 1);
    }}
    sx={{
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "auto",
      backgroundColor: "#ffffff",
      position: "relative",
    }}
  >
        <Table sx={{ minWidth: TABLE_WIDTH }}>
          {/* {openRowKey === null && ( remove heder on expanded row view */}
          <TableHead 
            sx={{ 
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#f1f3f4",
            }}
          >
            <TableRow sx={{
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
            }}>
          {[
            "TYPE",
            "ITINERARY ID",
            "TRIP TYPE",
            "SUPPLIER CONF#",
            "OUTBOUND",
            "INBOUND",
            "CLIENT",
            "BOOKING DATE",
            "AMOUNT",
          ].map((label) => (
            <TableCell
              key={label}
              align="center"
              sx={{
                backgroundColor: "#f1f3f4",
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
              }}
            >
              {label}
            </TableCell>
          ))}
          <TableCell
  align="center"
  sx={{
    ...stickyHeaderBase,
    right: 228,
    zIndex: 12,
    backgroundColor: "#f1f3f4",
    fontWeight: 700,
    whiteSpace: "nowrap",
    boxShadow: showRightShadow
      ? "inset 9px 0 12px -10px rgb(0 0 0 / 14%)"
      : "none",
    transition: "box-shadow 0.2s ease",
  }}
>
  PAYMENT STATUS
</TableCell>
<TableCell
  align="center"
  sx={{
    ...stickyHeaderBase,
    right: 65,
    zIndex: 12,
    boxShadow: "-2px 0 6px rgba(0,0,0,0.15)",
    fontWeight: 700,
                whiteSpace: "nowrap",
  }}
>
  BOOKING STATUS
</TableCell>
<TableCell
  align="center"
  sx={{
    ...stickyHeaderBase,
    right: 0,
    zIndex: 13,
    boxShadow: "-3px 0 8px rgba(0,0,0,0.2)",
    fontWeight: 700,
                whiteSpace: "nowrap",
  }}
>
  ACTIONS
</TableCell>
          
        </TableRow>
          </TableHead>
          {/* )} */}

          <TableBody>
           
            {filteredRows.map((row, index) => {
              const hasStartDate = Boolean(row.startDate?.trim());
              const hasEndDate = Boolean(row.endDate?.trim());
              const hasBookDate = Boolean(row.bkdate?.trim());
              const priceColor = getPriceColor(row.paymentStatus);
              const tripTypeNumber = Number(row.tripType);
              return (
              <React.Fragment key={`${row.itineraryId}-${index}`}>
                <TableRow id={`transaction-row-${index}`} hover onClick={() => handleRowClick(row, index)} sx={{ cursor: "pointer" }}>
  <TableCell align="center">
    <Box display="flex" alignItems="center" justifyContent="center">
                      {getTypeIcon(row.productType, row.itineraryId, itineraryTypeMap) || "-"}
                    </Box>
  </TableCell>
  <TableCell align="center">{addHyphenAfterThree(row.itineraryId) || "-"}</TableCell>
<TableCell align="center">
  {getTripTypeDisplay(row)}
</TableCell>
<TableCell align="center">{row.sup_conf_Id || "-"}</TableCell>
 <TableCell align="center">
                    <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                      {hasStartDate && (
                      <CalendarMonth fontSize="small" sx={{ color: "#059669" }} />
                      )}
                      {/* {formatDate(row.startDate) || "-"} */}
                      {hasStartDate ? formatDate(row.startDate) : "-"}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                      {tripTypeNumber !== 1 && hasEndDate && (
                      <CalendarMonth fontSize="small" sx={{ color: "#059669" }} />
                      )}
                      {/* {formatDate(row.endDate) || "-"} */}
                      {tripTypeNumber === 1 ? "-" : hasEndDate ? formatDate(row.endDate) : "-"}
                      {/* {hasEndDate ? formatDate(row.endDate) : "-"} */}
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <span>{capitalize(row.name) || "-"}</span>
                      <EnrichmentBadges row={row} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                      {hasBookDate && (
                      <InsertDriveFile fontSize="small" sx={{ color: "#7c3aed" }} />
                      )}
                      {/* {formatDate(row.bkdate) || "-"} */}
                      {hasBookDate ? formatDate(row.bkdate) : "-"}
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">${row.price || "-"}</TableCell>
<TableCell align="center"  sx={{ ...stickyRightBase, right: 228, boxShadow: showRightShadow
      ? "inset 9px 0 12px -10px rgb(0 0 0 / 14%)"
      : "none", transition: "box-shadow 0.2s ease", color: priceColor.textColor, paddingLeft: "0 !important", paddingRight: "0 !important", }}>
  {capitalize(row.paymentStatus) || "-"}
</TableCell>
                  <TableCell align="center" sx={{
    ...stickyRightBase,
    right: 65,
    boxShadow: "-2px 0 6px rgba(0,0,0,0.12)",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
  }}>
                    <Box sx={{
                        padding: "6px 0px",
                        color: priceColor.textColor,
                        // borderRadius: "25px",
                        margin: "0 12px",
                        // backgroundColor: priceColor.bgColor,
                        // border: `1px solid ${priceColor.borderColor}`,
                        // fontWeight: 600,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5
                      }}>
                        {getStatusIcon(row.paymentStatus)}
                    {capitalize(row.bkStatus) || "-"}</Box></TableCell>
                    <TableCell align="center" sx={{
    ...stickyRightBase,
    right: 0,
    zIndex: 6,
    boxShadow: "-3px 0 8px rgba(0,0,0,0.18)",
  }}>
                      {row.productType === "flight" && row.modifyTransactionId && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModifyContext({
                              modifyTransactionId: row.modifyTransactionId as string,
                              itineraryId: row.itineraryId,
                              customerName: row.name,
                              customerEmail: row.email,
                            });
                          }}
                          sx={{
                            mr: 1,
                            textTransform: "none",
                            borderColor: "#fed7aa",
                            background: "#fffbeb",
                            color: "#d97706",
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            minWidth: 0,
                            "&:hover": { background: "#fef3c7", borderColor: "#fed7aa" },
                          }}
                        >
                          {row.reqType === "refund" ? "View refund" : "Process modify"}
                        </Button>
                      )}
                      {row.productType === "flight" && row.cancelTransactionId && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCancelContext({
                              cancelTransactionId: row.cancelTransactionId as string,
                              itineraryId: row.itineraryId,
                              customerName: row.name,
                              customerEmail: row.email,
                            });
                          }}
                          sx={{
                            mr: 1,
                            textTransform: "none",
                            borderColor: "#fecaca",
                            background: "#fef2f2",
                            color: "#dc2626",
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            minWidth: 0,
                            "&:hover": { background: "#fee2e2", borderColor: "#fecaca" },
                          }}
                        >
                          Process cancel
                        </Button>
                      )}
                      {/* No ops-initiated cancel: modify and cancel are both raised
                          by the customer from the MyTrip app (modify doc, "Who does
                          what"). Ops only PROCESSES them -- the Process modify /
                          Process cancel buttons above appear once the customer's
                          request exists (modify/cancelTransactionId present). */}
                      <IconButton
                      color="primary"
                      size="small"
                      >
                      <Visibility />
                      </IconButton>
                      <IconButton
                      color="secondary"
                      size="small"
                      >
                       <Edit />
                    </IconButton>
                </TableCell>
                </TableRow>
                <TableRow>
  <TableCell colSpan={12} sx={{ p: 0 }}>
    <Collapse in={openRowKey === getRowKey(row)} timeout="auto" unmountOnExit>
      <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
        {detailsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems:'center' }}> <CircularProgress /> </Box>}

        {!detailsLoading &&
          detailsMap[getRowKey(row)] &&
          (row.productType === "flight" ? (
            <FlightDetails data={detailsMap[getRowKey(row)]} />
          ) : (
            <HotelDetails data={detailsMap[getRowKey(row)]} />
          ))}
      </Box>
    </Collapse>
  </TableCell>
</TableRow>
              </React.Fragment>
              );
})}
            {!loading && filteredRows.length === 0 && (
    <TableRow>
      <TableCell colSpan={12} align="center">
        <Typography variant="body2" color="text.secondary" sx={{
            height: 300,                // controls vertical centering
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
          }}>
          No Data Match
        </Typography>
      </TableCell>
    </TableRow>
  )}
 {loading && (
    <TableRow>
      <TableCell colSpan={12} align="center">
        <Box
          sx={{
            height: 300,                // controls vertical centering
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </TableCell>
    </TableRow>
  )}
            {!hasMore && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No transactions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        /> */}
        {/* <Box
    ref={bottomScrollRef}
    onScroll={syncFromBottom}
    sx={{
      height: 16,
      overflowX: "auto",
      overflowY: "hidden",
      borderTop: "1px solid #e5e7eb",
      backgroundColor: "red"
    }}
  >
    
  </Box> */}
      </Box>
    </Box>
  );
}
